import { FC, useEffect, useState } from "react";
import { Spin, Switch, message } from "../../design-system";
import { handleError } from "../../common/ErrorHandling";
import {
    getNotificationSettings,
    updateNotificationSettings,
} from "../../common/api/NotificationsClient";
import {
    NOTIFICATION_TYPE_OPTIONS,
    NotificationSettings,
    NotificationType,
} from "../../types/NotificationTypes";
import classes from "./NotificationSettingsCard.module.less";

/**
 * The user's say over their notification emails (plans/notifications.md,
 * section 3): one master switch, one toggle per type, saved as they are
 * flipped. The Notifications page is not affected: it records everything.
 */
export const NotificationSettingsCard: FC = () => {
    const [settings, setSettings] = useState<NotificationSettings>();

    useEffect(() => {
        getNotificationSettings().then(setSettings).catch(handleError);
    }, []);

    const save = async (next: NotificationSettings) => {
        const previous = settings;
        setSettings(next);
        try {
            setSettings(await updateNotificationSettings(next));
            void message.success("Notification settings saved");
        } catch (error: any) {
            setSettings(previous);
            handleError(error);
        }
    };

    const toggleType = (type: NotificationType, enabled: boolean) => {
        if (!settings) return;
        const disabledTypes = enabled
            ? settings.disabledTypes.filter((t) => t !== type)
            : [...settings.disabledTypes, type];
        void save({ ...settings, disabledTypes });
    };

    return (
        <div>
            <h3 className={classes.title}>Notifications</h3>
            <Spin spinning={!settings}>
                <div className={classes.card}>
                    <div className={classes.row}>
                        <div>
                            <div className={classes.rowLabel}>Send me emails</div>
                            <div className={classes.rowHint}>
                                Off means no email at all. Everything still shows on your
                                Notifications page.
                            </div>
                        </div>
                        <Switch
                            checked={settings?.emailEnabled ?? true}
                            onChange={(emailEnabled) =>
                                settings && save({ ...settings, emailEnabled })
                            }
                        />
                    </div>
                </div>
                <div className={classes.card}>
                    {NOTIFICATION_TYPE_OPTIONS.map((option) => (
                        <div key={option.type} className={classes.row}>
                            <div>
                                <div className={classes.rowLabel}>{option.label}</div>
                                <div className={classes.rowHint}>{option.description}</div>
                            </div>
                            <Switch
                                size="small"
                                disabled={!settings?.emailEnabled}
                                checked={!settings?.disabledTypes.includes(option.type)}
                                onChange={(enabled) => toggleType(option.type, enabled)}
                            />
                        </div>
                    ))}
                </div>
            </Spin>
        </div>
    );
};
